import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, CheckCircle, AlertCircle, Calendar, Package, Trash2, Clock, Sparkles, Grid, List } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { renderCell, renderCellDate, cellText } from '../../utils/helpers';

const CLIENT_NOTIFICATION_INFO = [
  {
    icon: AlertCircle,
    bg: 'bg-red-500/20',
    text: 'text-red-600',
    title: 'Alertes d\'expiration',
    description: 'Lorsqu\'un équipement arrive à expiration',
  },
  {
    icon: Calendar,
    bg: 'bg-amber-500/20',
    text: 'text-amber-600',
    title: 'Maintenances prévues',
    description: 'Rappels pour les maintenances à venir',
  },
  {
    icon: Package,
    bg: 'bg-blue-500/20',
    text: 'text-blue-600',
    title: 'Nouveaux équipements',
    description: 'Ajout de nouveaux équipements',
  },
  {
    icon: CheckCircle,
    bg: 'bg-green-500/20',
    text: 'text-green-600',
    title: 'Rapports disponibles',
    description: 'Nouveaux rapports prêts à télécharger',
  },
];

const ADMIN_NOTIFICATION_INFO = [
  {
    icon: AlertCircle,
    bg: 'bg-red-500/20',
    text: 'text-red-600',
    title: 'Alertes d\'expiration',
    description: 'Équipements proches de la date de maintenance',
  },
  {
    icon: Calendar,
    bg: 'bg-amber-500/20',
    text: 'text-amber-600',
    title: 'Maintenances prévues',
    description: 'Nouvelles maintenances planifiées pour un client',
  },
  {
    icon: AlertCircle,
    bg: 'bg-violet-500/20',
    text: 'text-violet-600',
    title: 'Changements de statut',
    description: 'Statut modifié sur un équipement, une maintenance ou une armoire',
  },
];

const TYPE_LABELS = {
  maintenance_due: 'Maintenance',
  expiration_warning: 'Expiration',
  equipment_added: 'Équipement',
  report_ready: 'Rapport',
  status_changed: 'Statut',
  general: 'Général',
};

const Notifications = () => {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  const { isAdmin } = useAuth();
  const notificationInfoItems = isAdmin ? ADMIN_NOTIFICATION_INFO : CLIENT_NOTIFICATION_INFO;

  const [viewMode, setViewMode] = useState('table');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, title: '' });

  const getNotificationTypeLabel = (type) => TYPE_LABELS[type] || TYPE_LABELS.general;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'maintenance_due':
        return { icon: Calendar, color: 'from-amber-500 to-orange-600', bg: 'bg-amber-500/20', text: 'text-amber-600' };
      case 'expiration_warning':
        return { icon: AlertCircle, color: 'from-red-500 to-rose-600', bg: 'bg-red-500/20', text: 'text-red-600' };
      case 'equipment_added':
        return { icon: Package, color: 'from-blue-500 to-indigo-600', bg: 'bg-blue-500/20', text: 'text-blue-600' };
      case 'report_ready':
        return { icon: CheckCircle, color: 'from-green-500 to-emerald-600', bg: 'bg-green-500/20', text: 'text-green-600' };
      case 'status_changed':
        return { icon: AlertCircle, color: 'from-violet-500 to-purple-600', bg: 'bg-violet-500/20', text: 'text-violet-600' };
      default:
        return { icon: Bell, color: 'from-gray-500 to-gray-600', bg: 'bg-gray-500/20', text: 'text-gray-600' };
    }
  };

  const getNotificationStyle = (type, isRead) => {
    const baseStyle = getNotificationIcon(type);
    return {
      ...baseStyle,
      borderColor: isRead ? 'border-gray-200' : 'border-red-200',
      bgGradient: isRead ? 'bg-white' : 'bg-gradient-to-r from-red-50/50 to-white',
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="rounded-full h-16 w-16 border-4 border-red-200 border-t-red-600 shadow-lg"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative bg-gradient-to-r from-red-500 to-red-600 rounded-3xl shadow-2xl shadow-red-500/30 p-8 overflow-hidden"
      >
        {/* Background Decoration */}
        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
        
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-4">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl"
            >
              <Bell className="w-8 h-8 text-white" />
            </motion.div>
            <div>
              <h1 className="text-4xl font-extrabold text-white mb-2">Notifications</h1>
              <p className="text-red-100 text-lg font-semibold flex items-center gap-2">
                {unreadCount > 0 ? (
                  <>
                    <Sparkles className="w-5 h-5" />
                    <span>{unreadCount} nouvelle{unreadCount > 1 ? 's' : ''} notification{unreadCount > 1 ? 's' : ''}</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Tout est à jour</span>
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={viewMode === 'table' ? 'secondary' : 'outline'}
              size="sm"
              icon={List}
              onClick={() => setViewMode('table')}
            >
              Tableau
            </Button>
            <Button
              variant={viewMode === 'card' ? 'secondary' : 'outline'}
              size="sm"
              icon={Grid}
              onClick={() => setViewMode('card')}
            >
              Cartes
            </Button>
            {notifications.length > 0 && unreadCount > 0 && (
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  variant="secondary" 
                  onClick={markAllAsRead}
                  icon={CheckCircle}
                  className="shadow-xl"
                >
                  Tout marquer comme lu
                </Button>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Empty State */}
      {notifications.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200">
            <div className="text-center py-16">
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="inline-block p-6 bg-gradient-to-br from-red-500/10 to-red-600/10 rounded-full mb-6"
              >
                <Bell className="w-20 h-20 text-red-500" />
              </motion.div>
              <h3 className="text-2xl font-extrabold text-gray-900 mb-3">
                Aucune notification
              </h3>
              <p className="text-gray-600 font-medium text-lg">
                Vous serez notifié ici pour toutes les mises à jour importantes
              </p>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Table View */}
      {viewMode === 'table' && notifications.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Titre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notifications.map((notification, index) => {
                  const style = getNotificationStyle(notification.type, notification.read);
                  const IconComponent = style.icon;
                  return (
                    <motion.tr
                      key={notification.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className={`hover:bg-gray-50 transition-colors ${!notification.read ? 'bg-red-50/30' : ''}`}
                    >
                      <td className="px-6 py-4">
                        {!notification.read ? (
                          <span className="inline-flex px-2 py-1 text-xs font-bold bg-red-100 text-red-800 rounded-full">
                            Nouveau
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full">
                            Lu
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className={`flex items-center justify-center w-10 h-10 shrink-0 ${style.bg} rounded-lg`}>
                            <IconComponent className={`w-5 h-5 ${style.text}`} />
                          </div>
                          <span className="text-xs font-semibold text-gray-600">
                            {getNotificationTypeLabel(notification.type)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold text-gray-900">
                          {renderCell(notification.title)}
                        </span>
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        <span
                          className="text-sm text-gray-700 line-clamp-2"
                          title={cellText(notification.message)}
                        >
                          {renderCell(notification.message)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                        {renderCellDate(notification.createdAt, 'dd/MM/yyyy HH:mm')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2 min-w-[4.5rem]">
                          {!notification.read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Marquer comme lu"
                              aria-label="Marquer comme lu"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() =>
                              setDeleteConfirm({
                                open: true,
                                id: notification.id,
                                title: cellText(notification.title),
                              })
                            }
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer"
                            aria-label="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Card View */}
      {viewMode === 'card' && notifications.length > 0 && (
        <AnimatePresence mode="popLayout">
          <div className="space-y-4">
            {notifications.map((notification, index) => {
              const style = getNotificationStyle(notification.type, notification.read);
              const IconComponent = style.icon;
              
              return (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05, type: 'spring', stiffness: 100 }}
                  layout
                >
                  <motion.div
                    whileHover={{ scale: 1.02, y: -2 }}
                    className={`relative overflow-hidden ${style.bgGradient} rounded-2xl shadow-lg border-2 ${style.borderColor} p-6 hover:shadow-2xl transition-all duration-300`}
                  >
                    <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${style.color}`} />
                    
                    <div className="relative flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        {/* Icon */}
                        <motion.div
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.6 }}
                          className={`flex-shrink-0 p-3 ${style.bg} rounded-2xl group-hover:scale-110 transition-transform`}
                        >
                          <IconComponent className={`w-6 h-6 ${style.text}`} />
                        </motion.div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-3 gap-2">
                            <h3 className="text-lg font-extrabold text-gray-900">
                              {renderCell(notification.title)}
                            </h3>
                            {!notification.read && (
                              <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="ml-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/50"
                              >
                                <Sparkles className="w-3 h-3 mr-1" />
                                Nouveau
                              </motion.span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mb-3 font-medium leading-relaxed">
                            {renderCell(notification.message)}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 font-semibold">
                            <span className="inline-flex items-center gap-1.5">
                              <IconComponent className={`w-3.5 h-3.5 ${style.text}`} />
                              {getNotificationTypeLabel(notification.type)}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <Clock className="w-4 h-4" />
                              {renderCellDate(notification.createdAt, 'dd/MM/yyyy HH:mm')}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 ml-4">
                        {!notification.read && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => markAsRead(notification.id)}
                            className="p-2.5 bg-green-50 hover:bg-green-100 text-green-600 rounded-xl transition-all shadow-md hover:shadow-lg"
                            title="Marquer comme lu"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </motion.button>
                        )}
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() =>
                            setDeleteConfirm({
                              open: true,
                              id: notification.id,
                              title: cellText(notification.title),
                            })
                          }
                          className="p-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-all shadow-md hover:shadow-lg"
                          title="Supprimer"
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-5 h-5" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </AnimatePresence>
      )}

      {/* Notification Types Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-lg shadow-red-500/30">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900">Types de notifications</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            {notificationInfoItems.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -4 }}
                className="p-5 bg-white rounded-2xl border-2 border-gray-200 shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className={`flex-shrink-0 p-3 ${item.bg} rounded-2xl group-hover:scale-110 transition-transform`}
                  >
                    <item.icon className={`w-6 h-6 ${item.text}`} />
                  </motion.div>
                  <div className="flex-1">
                    <h3 className="font-extrabold text-gray-900 mb-1 text-base">{item.title}</h3>
                    <p className="text-sm text-gray-600 font-medium leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Card>
      </motion.div>

      <ConfirmDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, title: '' })}
        onConfirm={() => deleteNotification(deleteConfirm.id)}
        title="Supprimer la notification"
        message={`Supprimer « ${deleteConfirm.title} » ? Cette action est irréversible.`}
        confirmText="Supprimer"
        variant="danger"
      />
    </div>
  );
};

export default Notifications;

